import React, { useCallback, useContext, useMemo, useState } from 'react';
import _ from 'lodash';
import type { ColDef, GridApi, SortModelItem } from 'ag-grid-community';

import DataTable from 'components/DataTable';
import DataTableRenderers from 'components/DataTable/DataTable.renderers';
import { ShimSortModel } from 'components/DataTable/agParamsShim';
import useInfiniteRows from 'components/DataTable/useInfiniteRows';
import DetailsSlideOut from 'components/DetailsSlideOut';
import buildGridOptions from 'components/Grid/buildGridOptions';
import useGridFilterInput from 'components/Grid/useGridFilterInput';
import useGridTotalsRow from 'components/Grid/useGridTotalsRow';
import { RowHeightContext } from 'contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';
import DetailsSlideOutContext from 'contexts/DetailsSlideOutContext';
import GridContext from 'contexts/GridContext';
import GridDataSourceContext from 'contexts/GridDataSourceContext';
import useColumnDefs from 'hooks/useColumnDefs';
import usePopup from 'hooks/usePopup';

import { Z_INDEX } from '../../constants';

const EMPTY_TOTALS: DataTable.Row[] = [];
const EMPTY_SORT: SimpleGridSort[] = [];

const findColDef = ({
  columnDefs,
  colId,
}: {
  columnDefs: ColDef[];
  colId: string;
}) => columnDefs.find((c) => (c.colId ?? c.field) === colId);

const isPinnedLeft = (c: ColDef) => c.pinned === true || c.pinned === 'left';

// The second column shown (AG's getAllDisplayedColumns()[1]): left-pinned
// columns come first, right-pinned ones last, hidden ones not at all
const getSecondShownColumnId = (columnDefs: ColDef[]) => {
  const shown = columnDefs.filter((c) => !c.hide);
  const second = [
    ...shown.filter(isPinnedLeft),
    ...shown.filter((c) => !isPinnedLeft(c) && c.pinned !== 'right'),
    ...shown.filter((c) => c.pinned === 'right'),
  ][1];
  return second ? second.colId ?? second.field : undefined;
};

// A record's id, which follows it however the table is sorted or paged.
// Records from a special metric's own queries may have none: those keep
// their position
const getRecordId = (data: DataTable.Row, index: number) =>
  data.id !== undefined && data.id !== null ? `${data.id}` : `row-${index}`;

// A records grid (AG's GridContainer) drawn by DataTable: the base view's
// records a page at a time as the table nears the end of what's loaded,
// sorted by the server. From search2, or a special metric's own queries
// (GridContext's query overrides). Same column defs and renderers as the AG
// grid, with its totals row and order Details slide-out. Sits in a
// GridProvider, and publishes its export handle there for the export button.
const RecordsTable = ({
  baseView,
  sort = EMPTY_SORT,
  sizing,
  skipHeaderWhenSizing,
  isFlexLayout,
  isResizable,
  hasBorders,
  hasDarkHeader,
  isSearchScreen,
  hideTotalsRow,
  onExportHandle,
  fittedColumn,
  getContextMenuItems,
  onGridReady,
  slideOut,
}: {
  baseView: FleetOps.BaseView;
  // The sort the rows start with
  sort?: SimpleGridSort[];
  sizing: DataTable.SizingMode;
  skipHeaderWhenSizing?: boolean;
  // Columns share the width (flex), as useColumnDefs builds them for AG's
  // disableAutoLayout
  isFlexLayout?: boolean;
  isResizable?: boolean;
  hasBorders?: boolean;
  hasDarkHeader?: boolean;
  isSearchScreen?: boolean;
  hideTotalsRow?: boolean;
  onExportHandle?: (handle: DataTable.ExportHandle | undefined) => void;
  // fitWidth: the column sized by its content while the others share the
  // rest (AG's autoSizeFirstColumnOnly / autoSizeSecondColumnOnly)
  fittedColumn?: 'first' | 'second';
  // The right-click menu's items, written for AG (getContextMenuItems)
  getContextMenuItems?: (params: any) => DataTable.ContextMenuItem[];
  // For owners holding AG's GridApi (a board's drill highlight)
  onGridReady?: (event: { api: GridApi }) => void;
  // A slide-out beside the table (a board's), which the table narrows for
  slideOut?: React.ReactNode;
}) => {
  const {
    gridId,
    dataType,
    gridQueryOverride,
    gridCountQueryOverride,
    gridTotalsQueryOverride,
    setExportHandle,
  } = useContext(GridContext);
  const { rowHeight } = useContext(RowHeightContext);
  const columnDefs = useColumnDefs(baseView, undefined, sort, isFlexLayout);
  const defaultColDef = useMemo(
    () => buildGridOptions({ rowHeight }).defaultColDef,
    [rowHeight],
  );
  const { includeFields, filterInput } = useGridFilterInput({ baseView });
  const totalsRow: DataTable.Row[] | undefined = useGridTotalsRow({
    baseView,
    filterInput,
    gridTotalsQueryOverride,
    hideTotalsRow,
  });

  const [sortModel, setSortModel] = useState<SortModelItem[]>(() =>
    sort.map((s) => ({ colId: s.field, sort: s.direction })),
  );
  // The same sort again (a GridProvider re-applying its sortOverride) mustn't
  // reload the rows
  const onSortChanged = useCallback(
    (next: ShimSortModel) =>
      setSortModel((current) => (_.isEqual(current, next) ? current : next)),
    [],
  );
  const fittedColumnIds = useMemo(() => {
    if (fittedColumn !== 'second') {
      return undefined;
    }
    const id = getSecondShownColumnId(columnDefs);
    return id ? [id] : undefined;
  }, [columnDefs, fittedColumn]);
  const getColDef = useCallback(
    (colId: string) => findColDef({ columnDefs, colId }),
    [columnDefs],
  );
  // A special metric's records come from its own queries, a page at a time
  // by offset (AG's legacy ServerSideDataSource)
  const offsetQueries = useMemo(
    () =>
      gridQueryOverride && gridCountQueryOverride
        ? { rows: gridQueryOverride, count: gridCountQueryOverride }
        : undefined,
    [gridCountQueryOverride, gridQueryOverride],
  );
  const { rows, totalCount, isLoadingFirstPage, fetchNextPage } =
    useInfiniteRows({
      dataType,
      includeFields,
      filterInput,
      sortModel,
      getColDef,
      offsetQueries,
    });

  // StatusFlagCell counts the loaded documents for its totals row through
  // the grid's datasource; this is the same shape over our rows
  const dataSource = useMemo((): DataSource => {
    const ds: DataSource = {
      getRows: () => undefined,
      documents: rows,
      hasMoreRows: () => totalCount !== undefined && rows.length < totalCount,
      searchResult:
        totalCount === undefined
          ? undefined
          : { count: totalCount, searchAfter: '', sorting: sortModel },
    };
    return ds;
  }, [rows, sortModel, totalCount]);
  const gridOptions = useMemo(
    () => ({ serverSideDatasource: dataSource }),
    [dataSource],
  );

  const publishExportHandle = useCallback(
    (handle: DataTable.ExportHandle | undefined) => {
      if (setExportHandle) {
        setExportHandle(handle);
      }
      if (onExportHandle) {
        onExportHandle(handle);
      }
    },
    [onExportHandle, setExportHandle],
  );

  const [selectedOrderNumber, setSelectedOrderNumber] = useState<
    string | undefined
  >();
  const {
    isOpen: isDetailsSlideOpen,
    open: openDetailsSlide,
    close: closeDetailsSlide,
  } = usePopup();

  return (
    <DetailsSlideOutContext.Provider
      value={{
        selectedOrderNumber,
        setSelectedOrderNumber,
        isDetailsSlideOpen,
        openDetailsSlide,
        closeDetailsSlide,
      }}
    >
      <GridDataSourceContext.Provider value={{ dataSource }}>
        <div style={{ display: 'flex', height: '100%', width: '100%' }}>
          <div
            style={{
              height: '100%',
              width: isDetailsSlideOpen ? '70%' : '100%',
              // Narrows for a slide-out beside it
              minWidth: 0,
            }}
          >
            <DataTable
              tableId={gridId}
              testId="grid"
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              components={DataTableRenderers}
              rows={rows}
              totalsRows={totalsRow ?? EMPTY_TOTALS}
              gridOptions={gridOptions}
              sizing={sizing}
              skipHeaderWhenSizing={skipHeaderWhenSizing}
              isFirstColumnFitted={fittedColumn === 'first'}
              fittedColumnIds={fittedColumnIds}
              getRowId={getRecordId}
              getContextMenuItems={getContextMenuItems}
              onGridReady={onGridReady}
              isResizable={isResizable}
              hasBorders={hasBorders}
              hasDarkHeader={hasDarkHeader}
              isSearchScreen={isSearchScreen}
              isManualSorting
              onSortChanged={onSortChanged}
              isVirtualised
              onEndReached={fetchNextPage}
              isLoading={isLoadingFirstPage}
              noDataToDisplay={!isLoadingFirstPage && rows.length === 0}
              onExportHandle={publishExportHandle}
              totalRowCount={totalCount}
            />
          </div>
          {isDetailsSlideOpen && (
            <div
              style={{
                height: '100%',
                minWidth: 275,
                width: '30%',
                zIndex: Z_INDEX.GRID_SLIDE_OUT,
                fontSize: 11,
              }}
            >
              <DetailsSlideOut />
            </div>
          )}
          {/* A board's own slide-out comes from the board (slideOut), unlike
              AG's Grid, which draws it on every grid: importing it here would
              make a cycle */}
          {slideOut}
        </div>
      </GridDataSourceContext.Provider>
    </DetailsSlideOutContext.Provider>
  );
};

export default RecordsTable;
